<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import UiModal from '@/components/ui/UiModal.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiField from '@/components/ui/UiField.vue'
import SearchPicker from '@/components/form/SearchPicker.vue'

/**
 * The services a stage's acceptance starts (D-176), set in one place and confirmed: the
 * contract's pending activations to wait for this stage, or a new one made for it. Before it is
 * confirmed, what will follow for each is said — it waits for this stage (and the others it
 * waits for), or it no longer does, and one left waiting for nothing is started by hand.
 */
const props = defineProps({
  stage: { type: Object, default: null },
  activations: { type: Array, default: () => [] },
  stages: { type: Array, default: () => [] },
  serviceAgreements: { type: Array, default: () => [] },
  busy: Boolean,
})
const emit = defineEmits(['close', 'save'])
const { t } = useI18n()
const chosen = ref([])
const create = ref({ on: false, agreement: '', name: '' })
watch(() => props.stage, (s) => {
  if (!s) return
  chosen.value = (s.starts || []).filter((x) => x.status === 'pending').map((x) => x.uuid)
  create.value = { on: false, agreement: props.serviceAgreements[0]?.uuid || '', name: '' }
}, { immediate: true })
const options = computed(() => props.activations.map((x) => ({ value: x.uuid, label: `${x.agreement} · ${x.name}`, hint: t(`contract.triggers.${x.trigger}`) })))
const stageName = (u) => props.stages.find((s) => s.uuid === u)?.name || '?'
const before = computed(() => (props.stage?.starts || []).filter((x) => x.status === 'pending').map((x) => x.uuid))

const consequences = computed(() => {
  const s = props.stage
  if (!s) return []
  const out = []
  for (const x of props.activations) {
    const was = before.value.includes(x.uuid)
    const now = chosen.value.includes(x.uuid)
    const others = (x.trigger_stages || []).filter((u) => u !== s.uuid)
    const label = `${x.agreement} · ${x.name}`
    if (now && !was) {
      out.push({ tone: 'info', text: others.length && x.trigger === 'stage'
        ? t('contract.starts_rule.addedWithOthers', { activation: label, stages: [s.name, ...others.map(stageName)].join(', ') })
        : t('contract.starts_rule.added', { activation: label, stage: s.name }) })
      if (x.trigger !== 'stage') out.push({ tone: 'warn', text: t('contract.starts_rule.triggerChanges', { activation: label, was: t(`contract.triggers.${x.trigger}`) }) })
    } else if (was && !now) {
      out.push({ tone: others.length ? 'info' : 'warn', text: others.length
        ? t('contract.starts_rule.removedStillWaits', { activation: label, stages: others.map(stageName).join(', ') })
        : t('contract.starts_rule.removedManual', { activation: label }) })
    }
  }
  if (create.value.on) {
    const a = props.serviceAgreements.find((x) => x.uuid === create.value.agreement)
    out.push({ tone: 'info', text: t('contract.starts_rule.created', { agreement: a ? a.number : '', name: create.value.name || t('contract.activationFor', { stage: s.name }) }) })
  }
  if (!out.length) out.push({ tone: 'info', text: t('contract.starts_rule.nothing') })
  return out
})
const changed = computed(() => create.value.on || JSON.stringify([...chosen.value].sort()) !== JSON.stringify([...before.value].sort()))
function save() {
  emit('save', { activations: chosen.value, create: create.value.on ? { agreement: create.value.agreement, name: create.value.name.trim() || t('contract.activationFor', { stage: props.stage.name }) } : null })
}
</script>

<template>
  <UiModal :open="!!stage" :title="$t('contract.starts_rule.title', { name: stage?.name || '' })" size="lg" @close="emit('close')">
    <div v-if="stage" class="space-y-4" data-testid="stage-starts-dialog">
      <UiField :label="$t('contract.startsField')">
        <SearchPicker v-model="chosen" :options="options" :placeholder="$t('contract.startsSearch')" />
      </UiField>
      <p v-if="!activations.length" class="text-sm text-muted">{{ $t('contract.noPendingActivations') }}</p>
      <div v-if="serviceAgreements.length" class="rounded-lg border border-default p-3">
        <label class="flex items-center gap-2 text-sm"><input v-model="create.on" type="checkbox" class="accent-brand-600" data-testid="stage-starts-create" /> {{ $t('contract.newActivationOpen') }}</label>
        <div v-if="create.on" class="mt-2 grid gap-3 sm:grid-cols-2">
          <UiField :label="$t('contract.inAgreement')"><select v-model="create.agreement" class="input"><option v-for="a in serviceAgreements" :key="a.uuid" :value="a.uuid">{{ a.number }} · {{ a.title }}</option></select></UiField>
          <UiField :label="$t('common.name')"><input v-model="create.name" class="input" :placeholder="$t('contract.activationFor', { stage: stage.name })" /></UiField>
        </div>
      </div>
      <p v-else class="text-sm text-faint">{{ $t('contract.noServiceAgreement') }}</p>
      <section class="rounded-xl border border-default bg-[var(--surface-2)] p-3" data-testid="starts-consequences">
        <p class="label mb-1">{{ $t('contract.rule.whatHappens') }}</p>
        <ul class="space-y-1 text-sm">
          <li v-for="(c, i) in consequences" :key="i" :class="c.tone === 'warn' ? 'text-amber-800 dark:text-amber-200' : ''">
            <i :class="['fa-solid fa-fw', c.tone === 'warn' ? 'fa-triangle-exclamation' : 'fa-circle-info text-faint']" aria-hidden="true"></i> {{ c.text }}
          </li>
        </ul>
      </section>
    </div>
    <template #footer>
      <UiButton @click="emit('close')">{{ $t('common.cancel') }}</UiButton>
      <UiButton variant="primary" icon="fa-check" :loading="busy" :disabled="!changed" data-testid="starts-confirm" @click="save">{{ $t('contract.rule.confirm') }}</UiButton>
    </template>
  </UiModal>
</template>
